import { Injectable } from '@angular/core';
import { ClientMatterService } from './client-matter.service';
import { AuthService } from './auth.service';
import { LogService } from './log.service';
import { LogLevelsDictionary } from '../dictionaries/LogLevels';
import { UserRoles } from '../dictionaries/UserRoles';

export interface PatientAccessResult {
  hasAccess: boolean;
  accessType?: 'owner' | 'creator' | 'shared' | 'allowed_profile' | 'facility' | 'superuser';
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PatientAccessService {
  constructor(
    private clientMatterService: ClientMatterService,
    private authService: AuthService,
    private log_$: LogService
  ) {}

  /**
   * Validates if the current user has access to a patient.
   * Implements HIPAA-compliant access control checks.
   *
   * Access is granted if ANY of the following conditions are met:
   * 1. User is the owner of the patient record (ownerID matches)
   * 2. User created the patient record (uidOfCreator matches)
   * 3. User is in the patient's owners array (hierarchical ownership)
   * 4. User's email is in the patient's sharedWith array
   * 5. User's UID is in the patient's allowedProfiles array
   * 6. User is a facility user with access to the patient
   * 7. User is a Super User
   *
   * Access is DENIED if:
   * - User's UID is in the patient's restrictedProfiles array
   * - User is not authenticated
   * - Patient does not exist
   */
  async validatePatientAccess(patientId: string): Promise<PatientAccessResult> {
    try {
      // Check if user is authenticated
      if (!this.authService.userIsSignedIn.getValue()) {
        this.logAccessAttempt(patientId, null, false, 'User not authenticated');
        return { hasAccess: false, error: 'User not authenticated' };
      }

      // Check if userData is loaded
      const userData = this.authService.userData.getValue();
      if (!userData || Object.keys(userData).length === 0) {
        this.logAccessAttempt(patientId, null, false, 'User data not loaded');
        return { hasAccess: false, error: 'User data not loaded' };
      }

      const userUid = userData['uid'];
      const userEmail = userData['email'];
      const userRole = userData['role'];

      // Super User has access to everything
      if (userRole === UserRoles.superuser) {
        this.logAccessAttempt(patientId, userUid, true, 'Super User access');
        return { hasAccess: true, accessType: 'superuser' };
      }

      // Fetch patient data
      const patientData = await this.clientMatterService.getPatientOverviewData(patientId);

      if (!patientData) {
        this.logAccessAttempt(patientId, userUid, false, 'Patient not found');
        return { hasAccess: false, error: 'Patient not found' };
      }

      // Check if user is in restrictedProfiles (explicit denial)
      if (this.isInRestrictedProfiles(userUid, patientData)) {
        this.logAccessAttempt(patientId, userUid, false, 'User in restricted profiles');
        return { hasAccess: false, error: 'Access denied - restricted' };
      }

      // Check if user is the owner
      if (this.isOwner(userUid, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Owner access');
        return { hasAccess: true, accessType: 'owner' };
      }

      // Check if user is the creator
      if (this.isCreator(userUid, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Creator access');
        return { hasAccess: true, accessType: 'creator' };
      }

      // Check if user is in owners array (hierarchical ownership)
      if (this.isInOwnersArray(userUid, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Hierarchical owner access');
        return { hasAccess: true, accessType: 'owner' };
      }

      // Check if user's email is in sharedWith array
      if (this.isInSharedWith(userEmail, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Shared access');
        return { hasAccess: true, accessType: 'shared' };
      }

      // Check if user is in allowedProfiles
      if (this.isInAllowedProfiles(userUid, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Allowed profile access');
        return { hasAccess: true, accessType: 'allowed_profile' };
      }

      // Check facility-based access
      if (await this.hasFacilityAccess(userData, patientData)) {
        this.logAccessAttempt(patientId, userUid, true, 'Facility access');
        return { hasAccess: true, accessType: 'facility' };
      }

      // No access granted - log and deny
      this.logAccessAttempt(patientId, userUid, false, 'No matching access rule');
      return { hasAccess: false, error: 'Access denied' };

    } catch (error) {
      const userData = this.authService.userData.getValue();
      const userUid = userData?.['uid'] || 'unknown';
      this.logAccessAttempt(patientId, userUid, false, `Error: ${error.message}`);
      return { hasAccess: false, error: 'Error validating access' };
    }
  }

  /**
   * Check if user is the direct owner of the patient record
   */
  private isOwner(userUid: string, patientData: any): boolean {
    return patientData.ownerID === userUid;
  }

  /**
   * Check if user created the patient record
   */
  private isCreator(userUid: string, patientData: any): boolean {
    return patientData.uidOfCreator === userUid;
  }

  /**
   * Check if user is in the owners array (hierarchical ownership)
   */
  private isInOwnersArray(userUid: string, patientData: any): boolean {
    if (!patientData.owners || !Array.isArray(patientData.owners)) {
      return false;
    }
    return patientData.owners.includes(userUid);
  }

  /**
   * Check if user's email is in the sharedWith array
   */
  private isInSharedWith(userEmail: string, patientData: any): boolean {
    if (!patientData.sharedWith || !Array.isArray(patientData.sharedWith)) {
      return false;
    }

    // sharedWith can contain strings (emails) or objects with facility info
    return patientData.sharedWith.some(item => {
      if (typeof item === 'string') {
        return item.toLowerCase() === userEmail?.toLowerCase();
      }
      // Handle object format if needed
      return false;
    });
  }

  /**
   * Check if user is in the allowedProfiles array
   */
  private isInAllowedProfiles(userUid: string, patientData: any): boolean {
    if (!patientData.allowedProfiles || !Array.isArray(patientData.allowedProfiles)) {
      return false;
    }
    return patientData.allowedProfiles.includes(userUid);
  }

  /**
   * Check if user is in the restrictedProfiles array (explicit denial)
   */
  private isInRestrictedProfiles(userUid: string, patientData: any): boolean {
    if (!patientData.restrictedProfiles || !Array.isArray(patientData.restrictedProfiles)) {
      return false;
    }
    return patientData.restrictedProfiles.includes(userUid);
  }

  /**
   * Check facility-based access.
   * Facility users can access patients shared with their facility.
   */
  private async hasFacilityAccess(userData: any, patientData: any): Promise<boolean> {
    // Check if user is a facility user
    if (!userData.facilityuser || !userData.facility) {
      return false;
    }

    const userFacility = userData.facility.toLowerCase();

    // Check sharedWithFacilityMap (flat map set during C-MOVE sends)
    if (patientData.sharedWithFacilityMap && userFacility in patientData.sharedWithFacilityMap) {
      return true;
    }

    // Check sharedWith.facilities (object format: {consultants: [], facilities: [{facility, hub}]})
    if (patientData.sharedWith && typeof patientData.sharedWith === 'object' &&
        !Array.isArray(patientData.sharedWith) &&
        Array.isArray(patientData.sharedWith.facilities)) {
      return patientData.sharedWith.facilities.some(item =>
        typeof item === 'object' && item.facility &&
        item.facility.toLowerCase() === userFacility
      );
    }

    // Check sharedWith as array (legacy format)
    if (patientData.sharedWith && Array.isArray(patientData.sharedWith)) {
      return patientData.sharedWith.some(item =>
        typeof item === 'object' && item.facility &&
        item.facility.toLowerCase() === userFacility
      );
    }

    return false;
  }

  /**
   * Log access attempts for HIPAA audit trail.
   * All access attempts (successful and failed) are logged.
   */
  private logAccessAttempt(
    patientId: string,
    userUid: string | null,
    granted: boolean,
    reason: string
  ): void {
    const userData = this.authService.userData.getValue() || {};

    this.log_$.storeLog(
      userData,
      granted ? LogLevelsDictionary.info : LogLevelsDictionary.warning,
      {
        action: 'PATIENT_ACCESS_ATTEMPT',
        patientId: patientId,
        userUid: userUid,
        accessGranted: granted,
        reason: reason,
        timestamp: new Date().toISOString(),
        component: 'PatientAccessService',
        method: 'validatePatientAccess'
      },
      granted ? 'Patient Access Granted' : 'Patient Access Denied'
    );
  }

  /**
   * Validate access and return patient data if access is granted.
   * This is a convenience method that combines access validation with data retrieval.
   */
  async getPatientWithAccessCheck(patientId: string): Promise<{ data: any; accessResult: PatientAccessResult }> {
    const accessResult = await this.validatePatientAccess(patientId);

    if (!accessResult.hasAccess) {
      return { data: null, accessResult };
    }

    const patientData = await this.clientMatterService.getPatientOverviewData(patientId);
    return { data: patientData, accessResult };
  }

  /**
   * Check if current user can modify (write) patient data.
   * More restrictive than read access - only owners, creators, and superusers can modify.
   */
  async validatePatientWriteAccess(patientId: string): Promise<PatientAccessResult> {
    const readAccess = await this.validatePatientAccess(patientId);

    if (!readAccess.hasAccess) {
      return readAccess;
    }

    // Only certain access types allow write operations
    const writeAllowedTypes = ['owner', 'creator', 'superuser'];
    if (writeAllowedTypes.includes(readAccess.accessType)) {
      return readAccess;
    }

    const userData = this.authService.userData.getValue();
    const userUid = userData?.['uid'] || 'unknown';
    this.logAccessAttempt(patientId, userUid, false, 'Write access denied - read-only access type');

    return {
      hasAccess: false,
      error: 'Write access denied - you have read-only access to this patient'
    };
  }

  /**
   * Check if current user can delete patient data.
   * Most restrictive - only owners and superusers can delete.
   */
  async validatePatientDeleteAccess(patientId: string): Promise<PatientAccessResult> {
    const readAccess = await this.validatePatientAccess(patientId);

    if (!readAccess.hasAccess) {
      return readAccess;
    }

    // Only owners and superusers can delete
    const deleteAllowedTypes = ['owner', 'superuser'];
    if (deleteAllowedTypes.includes(readAccess.accessType)) {
      return readAccess;
    }

    const userData = this.authService.userData.getValue();
    const userUid = userData?.['uid'] || 'unknown';
    this.logAccessAttempt(patientId, userUid, false, 'Delete access denied - insufficient permissions');

    return {
      hasAccess: false,
      error: 'Delete access denied - only owners can delete patient records'
    };
  }
}
